import { type CSSProperties, useCallback, useEffect, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { isForYouTabEnabled } from '@atlassian/rovo-for-you/for-you-tab-enabled';

import { MINI_MODAL_EXPANDED_HEIGHT } from '../../../constants/private';

export interface UseMiniModalTabHeightParams {
	isEmptyConvo: boolean;
	activeAction: unknown;
	screenType: string;
	isAutoSuggestSkillsVisible: boolean;
	topOffset: number;
}

export interface UseMiniModalTabHeightReturn {
	hasTabsView: boolean;
	tabShellHeightPx: number | null;
	setTabShellHeightPx: (height: number | null) => void;
	/** Captured height of the For You tab shell, used together with tabShellHeightPx
	 *  to compute the effective minHeight (max of both tab heights). */
	forYouShellHeightPx: number | null;
	setForYouShellHeightPx: (height: number | null) => void;
	/** Computes dynamic container styles given the current wrapperStyle from the resize hook. */
	getDynamicContainerStyle: (wrapperStyle?: CSSProperties) => CSSProperties;
}

/**
 * Custom hook for managing mini modal tab height state.
 *
 * This hook handles:
 * - Feature gate check for tabs view
 * - Capturing and clearing tab shell height
 * - Providing a function to compute dynamic container styles
 *
 * The `getDynamicContainerStyle` function is returned instead of the styles directly
 * because `wrapperStyle` is only available inside the resize render callback, not at
 * the component level where hooks must be called.
 */
export function useMiniModalTabHeight(
	params: UseMiniModalTabHeightParams,
): UseMiniModalTabHeightReturn {
	const { isEmptyConvo, activeAction, screenType, isAutoSuggestSkillsVisible, topOffset } = params;

	// Shell-level gate: the tab shell is sized for both spaces and pins, so this must match
	// whatever condition renders the tab strip (see `use-empty-state-render`).
	const isFypTabsEnabled = isForYouTabEnabled();
	const [tabShellHeightPx, setTabShellHeightPx] = useState<number | null>(null);
	const [forYouShellHeightPx, setForYouShellHeightPx] = useState<number | null>(null);

	// Clear the captured shell height when leaving the empty-conversation tabs view
	// (e.g. user starts a conversation) so the stale snapshot isn't applied during transitions.
	useEffect(() => {
		if (!isFypTabsEnabled || !isEmptyConvo) {
			setTabShellHeightPx(null);
			setForYouShellHeightPx(null);
		}
	}, [isEmptyConvo, isFypTabsEnabled]);

	const hasTabsView = isFypTabsEnabled && isEmptyConvo;

	// Whether the modal should expand to full height (conversation, skills, etc.).
	// Tabs view is excluded: it uses the shell-height snapshot instead of locking minHeight,
	// so the user can still resize the modal smaller.
	const shouldLockToExpandedHeight =
		Boolean(activeAction) ||
		screenType === 'conversation' ||
		screenType === 'skills' ||
		screenType === 'prompt-library' ||
		isAutoSuggestSkillsVisible;

	// On very small viewports with Nav4, use a nav-aware formula to prevent the default
	// opened modal height from overlapping the top navigation bar.
	// 3.5rem = Nav4 standard top nav bar height (56px at base 16px font size)
	// 1.5rem = bottom clearance from the floating shell container (space.300)
	// Falls back to the topOffset-based formula for legacy callers (topOffset !== 0).
	// fg is hoisted so exposure is always tracked regardless of topOffset value.
	const isSmallViewportNavAware = fg('rovo-button-minimodal-height-small-view');
	const defaultMaxHeight =
		topOffset === 0 && isSmallViewportNavAware
			? `min(${MINI_MODAL_EXPANDED_HEIGHT}, calc(100vh - 3.5rem - 1.5rem))`
			: `min(${MINI_MODAL_EXPANDED_HEIGHT}, calc(100vh - ${topOffset}px))`;
	const defaultMinHeight = shouldLockToExpandedHeight ? defaultMaxHeight : undefined;

	const getDynamicContainerStyle = useCallback(
		(wrapperStyle?: CSSProperties): CSSProperties => {
			const isManuallyResized = Boolean(wrapperStyle?.height);

			// Apply the captured shell heights to prevent layout shifts on tab switch.
			// Use the max of both tab heights so the modal never shrinks when switching
			// between tabs, but can still grow if one tab has more content.
			// Only apply when not manually resized and still in empty convo.
			const effectiveMinHeightPx =
				tabShellHeightPx === null && forYouShellHeightPx === null
					? null
					: Math.max(tabShellHeightPx ?? 0, forYouShellHeightPx ?? 0);
			const useTabShellSnapshot =
				hasTabsView && !isManuallyResized && effectiveMinHeightPx !== null && isEmptyConvo;

			return {
				...wrapperStyle,
				maxHeight: isManuallyResized ? undefined : defaultMaxHeight,
				minHeight: isManuallyResized ? undefined : defaultMinHeight,
				...(useTabShellSnapshot ? { minHeight: `${effectiveMinHeightPx}px` } : {}),
			};
		},
		[
			hasTabsView,
			tabShellHeightPx,
			forYouShellHeightPx,
			isEmptyConvo,
			defaultMaxHeight,
			defaultMinHeight,
		],
	);

	return {
		hasTabsView,
		tabShellHeightPx,
		setTabShellHeightPx,
		forYouShellHeightPx,
		setForYouShellHeightPx,
		getDynamicContainerStyle,
	};
}
